import { Add } from "@mui/icons-material"
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid2 as Grid,
    IconButton,
    Stack,
    TextField,
    Tooltip,
} from "@mui/material"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3"
import { isValid } from "date-fns"
import { useEffect, useState } from "react"
import { useCreateSensorInstall, useFindSensors } from "../../../api/SensorInstallApi"
import { NexDropzone } from "../../../components/Dropzone"
import { useSnackbarStore } from "../../../components/Snackbar/Snackbar"
import { LabelAndText } from "../../../components/Typography/Typography"
import { useNexusSensorIdOptional } from "../../../hooks/useNexusParams"
import { SensorType } from "../../../models/sensors/Sensor"
import { CreateSensorInstallInfo } from "../../../models/sensors/SensorInstallInfo"
import { TimeUtilsDateFns } from "../../../utils/TimeUtils"

export const CreateSensorInstallIconButton = () => {
    const sensorId = useNexusSensorIdOptional()
    const [dialogOpen, setDialogOpen] = useState(false)
    const [boresightFile, setBoresightFile] = useState<File>()
    const [selectedSensor, setSelectedSensor] = useState<SensorType>()
    const [errorMessage, setErrorMessage] = useState("")
    const [inputValidStartDate, setValidStartDate] = useState<Date | undefined>(new Date())

    const openSnackbar = useSnackbarStore((state) => state.handleOpen)

    const sensorListResult = useFindSensors()
    const sensorList = sensorListResult.data ?? []

    const useSensorInstallMutation = useCreateSensorInstall()

    useEffect(() => {
        return handleResetDialog
    }, [])

    useEffect(() => {
        if (sensorId && sensorList && dialogOpen) {
            setSelectedSensor(sensorList.find((s) => String(s.sensor_id) === sensorId))
        }
    }, [sensorId, sensorList, dialogOpen])

    const handleFileSelect = (files: File[]) => {
        // only one file is allowed on upload
        const file = files[0]
        setBoresightFile(file)
    }

    const handleResetDialog = () => {
        setSelectedSensor(undefined)
        setBoresightFile(undefined)
        setValidStartDate(new Date())
        setErrorMessage("")
        useSensorInstallMutation.reset()
    }

    const handleClose = () => {
        handleResetDialog()
        setDialogOpen(false)
    }

    return (
        <>
            <Tooltip title="Add">
                <IconButton onClick={() => setDialogOpen(true)}>
                    <Add fontSize="small" />
                </IconButton>
            </Tooltip>
            <Dialog open={dialogOpen} maxWidth={"sm"} fullWidth={true}>
                <DialogTitle>New Sensor Installation</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        <Box>
                            <Autocomplete
                                size="small"
                                value={selectedSensor ?? null}
                                options={sensorList}
                                getOptionKey={(opt) => opt.sensor_id!}
                                getOptionLabel={(opt) => `${opt.sensor_model} - ${opt.serial_number}`}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={"Sensor Model - Sensor Serial Number"}
                                        helperText={"Required"}
                                    />
                                )}
                                onChange={(_, value) => setSelectedSensor(value ?? undefined)}
                            />

                            <Collapse in={!!selectedSensor}>
                                <Card>
                                    <CardContent>
                                        <Grid container spacing={4}>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <LabelAndText showDivider={false} label="Model">
                                                    {selectedSensor?.sensor_model}
                                                </LabelAndText>
                                                <LabelAndText showDivider={false} label="Type">
                                                    {selectedSensor?.sensor_type}
                                                </LabelAndText>
                                                <LabelAndText showDivider={false} label="Serial Number">
                                                    {selectedSensor?.serial_number}
                                                </LabelAndText>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <LabelAndText showDivider={false} label="Notes">
                                                    {selectedSensor?.notes?.trim() || "None"}
                                                </LabelAndText>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Collapse>
                        </Box>

                        <Divider />

                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label="Valid Start Date"
                                value={inputValidStartDate ?? null}
                                onChange={(value) => setValidStartDate(value ?? undefined)}
                                slotProps={{
                                    field: { clearable: true },
                                    textField: {
                                        size: "small",
                                        helperText: "Required",
                                        error: !inputValidStartDate || !isValid(inputValidStartDate),
                                    },
                                }}
                                format={TimeUtilsDateFns.dayFormat}
                            />
                        </LocalizationProvider>

                        <NexDropzone
                            acceptedFileTypes={[".ini", ".boresight"]}
                            filesLimit={1}
                            maxFileSize={5e7}
                            files={boresightFile && [boresightFile]}
                            onChange={handleFileSelect}
                        />

                        {useSensorInstallMutation.isError && (
                            <Alert severity="error">
                                {errorMessage ?? "Unable to create new sensor installation!"}
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={!isValid(inputValidStartDate) || !boresightFile || !selectedSensor?.sensor_id}
                        variant="contained"
                        onClick={async () => {
                            const file = boresightFile
                            if (file && selectedSensor?.sensor_id && inputValidStartDate) {
                                const newDate = new Date()
                                const newSensorInstallInfo: CreateSensorInstallInfo = {
                                    sensor_id: selectedSensor.sensor_id,
                                    date_uploaded: TimeUtilsDateFns.formatDate(newDate),
                                    valid_start: TimeUtilsDateFns.formatDate(inputValidStartDate),
                                    file_name: file.name,
                                    file_data: await file.text(),
                                    file_size: file.size,
                                    file_format: file.type,
                                }
                                await useSensorInstallMutation.mutateAsync(newSensorInstallInfo, {
                                    onSuccess: async () => {
                                        setErrorMessage("")
                                        openSnackbar({
                                            message: `Created new Sensor Installation for sensor ${selectedSensor?.sensor_model} - ${selectedSensor?.serial_number}`,
                                            severity: "success",
                                        })
                                    },
                                    onError: (error) => {
                                        setErrorMessage(error.message)
                                    },
                                })
                            }
                            handleClose()
                        }}
                    >
                        Upload Installation
                    </Button>
                    <Button color="error" onClick={handleClose}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
